import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import "./cabina.css";

const fmt = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmt0 = (v) => (v == null ? "—" : `$${Math.round(Number(v)).toLocaleString("es-MX")}`);

// Ejemplos rápidos (del prototipo original)
const EJEMPLOS = [
  { l: "Vuelo internacional", cat: "VUE-COM", amb: "internacional_sale_mx", v: 129516, c: 108335, fijos: { otros_impuestos: 4688 } },
  { l: "Vuelo nacional", cat: "VUE-COM", amb: "nacional", v: 12000, c: 10400, fijos: { otros_impuestos: 2000 } },
  { l: "Hotel nacional", cat: "HOS-HAB", amb: "nacional", v: 11600, c: 9280, fijos: {} },
  { l: "Hotel con ISH y propinas", cat: "HOS-HAB", amb: "nacional", v: 12400, c: 9700, fijos: { ish: 220, propinas: 180 } },
  { l: "Hotel en el extranjero", cat: "HOS-HAB", amb: "extranjero", v: 12000, c: 8385, fijos: {} },
  { l: "Coffee break con propinas", cat: "AYB-CFB", amb: "nacional", v: 28000, c: 21500, fijos: { propinas: 1400, cargo_servicio: 900 } },
  { l: "Tour extranjero", cat: "PRG-TOU", amb: "extranjero", v: 9900, c: 7420, fijos: {} },
  { l: "Fee de agencia", cat: "COM-FEE", amb: "nacional", v: 15000, c: 0, fijos: {} },
  { l: "Margen negativo", cat: "HOS-HAB", amb: "nacional", v: 9000, c: 10400, fijos: {} },
];

export default function CabinaLaboratorio() {
  const [cat, setCat] = useState(null);
  const [categoria, setCategoria] = useState("VUE-COM");
  const [ambito, setAmbito] = useState("internacional_sale_mx");
  const [venta, setVenta] = useState(129516);
  const [costoTotal, setCostoTotal] = useState(108335);
  const [labD, setLabD] = useState({}); // desglose por componente (codigo → monto)
  const [prop, setProp] = useState(null); // {regla, propuesta}
  const [calc, setCalc] = useState(null);
  const [simActive, setSimActive] = useState(false);
  const [simTasa, setSimTasa] = useState(null); // override tasa_efectiva_costo (fracción)

  useEffect(() => { axios.get(`${API}/cabina/catalogo`).then((r) => setCat(r.data)); }, []);

  const catActual = useMemo(() => cat?.categorias?.find((c) => c.codigo === categoria), [cat, categoria]);
  const ambitos = catActual?.ambitos || ["nacional"];
  const esCostoSinVenta = !!catActual?.es_costo_sin_venta;

  // Buscar plantilla para la categoría actual
  const plantilla = useMemo(() => {
    if (!cat?.plantillas_costo) return null;
    const found = cat.plantillas_costo.find((p) => (p.aplica_categorias || []).includes(categoria));
    return found || cat.plantillas_costo.find((p) => p.codigo === "PL-GENERICO");
  }, [cat, categoria]);

  // Propone desglose desde costo_total + fijos (llamada al backend)
  const proponer = useCallback(async (cate, amb, costo, fijos = {}, tasaOverride = null) => {
    const body = { categoria_codigo: cate, ambito: amb, costo_total: Number(costo) || 0, ...fijos };
    if (tasaOverride != null) body.override_tasa_efectiva_costo = Number(tasaOverride);
    const r = await axios.post(`${API}/cabina/proponer-desglose`, body);
    return r.data;
  }, []);

  // Regenerar desglose desde costo_total (usa lo del backend)
  const regenerar = useCallback(async () => {
    if (!plantilla) return;
    // Toma los fijos ya capturados (impuestos/ish/propinas/cargo) de labD si existen
    const fijos = {
      otros_impuestos: labD.otros_impuestos || 0,
      ish: labD.ish || 0,
      propinas: labD.propinas || 0,
      cargo_servicio: labD.cargo_servicio || 0,
    };
    const tasaOverride = (simActive && simTasa != null) ? simTasa / 100 : null;
    const p = await proponer(categoria, ambito, costoTotal, fijos, tasaOverride);
    setProp(p);
    // Set labD con la propuesta
    const nuevo = {};
    (plantilla.componentes || []).forEach((c) => {
      nuevo[c.codigo] = p.propuesta[c.codigo] || 0;
    });
    setLabD(nuevo);
  }, [categoria, ambito, costoTotal, plantilla, proponer, simActive, simTasa, labD.otros_impuestos, labD.ish, labD.propinas, labD.cargo_servicio]);

  // Calcular fiscal a partir de labD editado
  const recalcular = useCallback(async () => {
    if (!plantilla) return;
    const tiposMap = {};
    (plantilla.componentes || []).forEach((c) => { tiposMap[c.codigo] = c.tipo; });
    const desglose = Object.entries(labD)
      .filter(([, v]) => (Number(v) || 0) !== 0)
      .map(([codigo, monto]) => ({ codigo, tipo: tiposMap[codigo] || "base_gravada", monto: Number(monto) }));
    try {
      // Consultar propuesta (para tener la regla actual, aún si el usuario no regenera)
      if (!prop) {
        const tasaOverride = (simActive && simTasa != null) ? simTasa / 100 : null;
        const p = await proponer(categoria, ambito, costoTotal, {}, tasaOverride);
        setProp(p);
      }
      const r = await axios.post(`${API}/cabina/calcular-fiscal`, {
        venta_total_mxn: Number(venta), desglose, es_costo_sin_venta: esCostoSinVenta,
      });
      setCalc(r.data);
    } catch (e) {
      toast.error("Error al calcular");
    }
  }, [labD, plantilla, prop, venta, categoria, ambito, costoTotal, esCostoSinVenta, simActive, simTasa, proponer]);

  // Al cambiar categoría/ámbito, regenerar propuesta desde costoTotal
  useEffect(() => { if (plantilla) regenerar(); /* eslint-disable-next-line */ }, [categoria, ambito, plantilla?.codigo]);

  // Recalcular cada vez que cambie labD, venta o simulación
  useEffect(() => { if (plantilla && Object.keys(labD).length > 0) recalcular(); /* eslint-disable-next-line */ }, [labD, venta]);

  // Cuando cambia la simulación, regenerar propuesta desde costo total
  useEffect(() => { if (plantilla) regenerar(); /* eslint-disable-next-line */ }, [simActive, simTasa]);

  const usarEjemplo = async (e) => {
    setCategoria(e.cat);
    setAmbito(e.amb);
    setVenta(e.v);
    setCostoTotal(e.c);
    // La regeneración correrá vía useEffect [categoria/ambito]
  };

  const suma = Object.values(labD).reduce((a, v) => a + (Number(v) || 0), 0);

  // Fiscal bar segmentos
  const segs = calc ? [
    { c: "g-cn", label: "Costo neto", v: calc.costo_neto },
    { c: "g-ia", label: "IVA acred.", v: calc.iva_acreditable },
    { c: "g-it", label: "Imp. terc.", v: calc.impuestos_terceros },
    { c: "g-ut", label: "Utilidad", v: calc.utilidad_venta },
    { c: "g-iu", label: "IVA utilidad", v: calc.iva_utilidad },
  ].filter((s) => s.v > 0) : [];
  const total = segs.reduce((a, s) => a + s.v, 0) || 1;

  // Categorías por grupo
  const grupos = {};
  (cat?.categorias || []).forEach((c) => { (grupos[c.grupo] = grupos[c.grupo] || []).push(c); });

  // Comparación método spreadsheet
  const viejo = Number(venta || 0) - Number(suma || 0);
  const dif = calc ? (viejo - (calc.utilidad_total || 0)) : 0;

  const reglaActual = prop?.regla;
  const tasaDefault = reglaActual ? (reglaActual.tasa_efectiva_costo || 0) * 100 : 16;

  return (
    <div className="cv" data-testid="cabina-laboratorio">
      <div className="head">
        <div>
          <h2>Laboratorio</h2>
          <p>Prueba cualquier desglose sin ensuciar los expedientes. Los campos cambian según la categoría. Compara contra cómo lo calcula hoy el spreadsheet.</p>
        </div>
      </div>

      <div className="split">
        <div>
          <div className="card">
            <div className="card-b">
              <div className="grid g2">
                <label className="f"><span>Categoría</span>
                  <select value={categoria} onChange={(e) => setCategoria(e.target.value)} data-testid="lab-select-categoria">
                    {Object.entries(grupos).map(([g, arr]) => (
                      <optgroup key={g} label={g}>{arr.map((c) => <option key={c.codigo} value={c.codigo}>{c.nombre}</option>)}</optgroup>
                    ))}
                  </select>
                </label>
                <label className="f"><span>Ámbito fiscal</span>
                  <select value={ambito} onChange={(e) => setAmbito(e.target.value)} data-testid="lab-select-ambito">
                    {ambitos.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
                <label className="f"><span>Venta al cliente</span><input type="number" value={venta ?? ""} onChange={(e) => setVenta(e.target.value === "" ? "" : e.target.value)} data-testid="lab-input-venta" /></label>
                <label className="f"><span>Costo del proveedor</span>
                  <input type="number" value={costoTotal ?? ""} onChange={(e) => setCostoTotal(e.target.value === "" ? "" : e.target.value)} data-testid="lab-input-costo" />
                  <div className="hint">Genera la propuesta de desglose vía la matriz.</div>
                </label>
              </div>

              {plantilla && (
                <>
                  <div style={{ fontSize: 10.5, letterSpacing: ".09em", textTransform: "uppercase", fontWeight: 800, color: "var(--cv-muted)", margin: "16px 0 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>Desglose del costo · plantilla {plantilla.nombre}</span>
                    <button className="btn ghost sm" onClick={regenerar} data-testid="lab-regenerar">
                      <RotateCcw size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
                      Regenerar propuesta
                    </button>
                  </div>
                  <div className="grid g2">
                    {(plantilla.componentes || []).map((c) => (
                      <label key={c.codigo} className="f">
                        <span>{c.nombre}</span>
                        <input type="number" step="0.01" value={labD[c.codigo] === "" || labD[c.codigo] == null ? "" : labD[c.codigo]}
                          onChange={(e) => setLabD({ ...labD, [c.codigo]: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                          data-testid={`lab-desglose-${c.codigo}`} />
                        <div className="hint">{c.tipo}</div>
                      </label>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, paddingTop: 9, borderTop: "1px solid var(--cv-line)" }}>
                    <span style={{ color: "var(--cv-muted)", fontWeight: 700, fontSize: 13 }}>Costo total</span>
                    <span className="mono" style={{ fontWeight: 600 }} data-testid="lab-suma-desglose">{fmt(suma)}</span>
                  </div>
                </>
              )}

              {segs.length > 0 && (
                <div className="fbar" style={{ marginTop: 18 }}>
                  <div className="fbar-t"><span>Desglose fiscal</span><span className="mono">{fmt(venta)}</span></div>
                  <div className="bar">
                    {segs.map((s, i) => (
                      <div key={i} className={`seg ${s.c}`} style={{ width: `${(s.v / total) * 100}%` }} title={`${s.label}: ${fmt(s.v)}`}>
                        {(s.v / total) * 100 > 8 && fmt(s.v)}
                      </div>
                    ))}
                  </div>
                  <div className="leg">
                    {segs.map((s, i) => <span key={i}><i className={s.c} />{s.label}: {fmt(s.v)}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Panel de simulación (modo sandbox) */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-h">
              <h3>Simulación de matriz</h3>
              <span className="sub">Sandbox: no toca la BD</span>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 12, color: "var(--cv-muted)", cursor: "pointer" }}>
                  <input type="checkbox" checked={simActive} onChange={(e) => { setSimActive(e.target.checked); if (!e.target.checked) setSimTasa(null); else setSimTasa(tasaDefault); }} style={{ width: "auto", marginRight: 6, verticalAlign: -1 }} data-testid="lab-sim-toggle" />
                  Activar simulación
                </label>
              </div>
            </div>
            <div className="card-b" style={{ opacity: simActive ? 1 : 0.5 }}>
              <div className="grid g2">
                <label className="f">
                  <span>Regla que aplica</span>
                  <input value={reglaActual ? `${reglaActual.codigo} · ${reglaActual.nombre}` : "—"} disabled />
                </label>
                <label className="f">
                  <span>Tasa efectiva de costo (%) — override</span>
                  <input type="number" step="0.01" min="0" max="50" value={simTasa ?? tasaDefault}
                    onChange={(e) => setSimTasa(parseFloat(e.target.value) || 0)}
                    disabled={!simActive}
                    data-testid="lab-sim-tasa" />
                  <div className="hint">Valor real: {tasaDefault.toFixed(2)}%. Al cambiarlo aquí, el laboratorio recalcula todo con este valor sin tocar la matriz. Para persistir, ve a la pestaña Matriz Fiscal.</div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div>
          {calc && prop && (
            <div className="card">
              <div className="card-h"><h3>Resultado</h3><span className="sub">motor §6.3 {simActive ? " · SIMULACIÓN" : ""}</span></div>
              <div className="card-b">
                <div className="rulebox" style={{ marginBottom: 14 }}>
                  <b>{prop.regla.codigo}</b> — {prop.regla.nombre}. {prop.regla.nota}
                  {prop.regla._simulada && <div style={{ marginTop: 6, color: "var(--cv-night)", fontWeight: 700 }}>· Regla simulada (override en memoria)</div>}
                </div>
                <div className="fout">
                  <div><div className="k">Costo total</div><div className="v mut">{fmt(calc.costo_total_mxn)}</div></div>
                  <div><div className="k">Costo neto</div><div className="v mut">{fmt(calc.costo_neto)}</div></div>
                  <div><div className="k">IVA acreditable</div><div className="v mut">{fmt(calc.iva_acreditable)}</div></div>
                  <div><div className="k">Base gravada</div><div className="v mut">{fmt(calc.base_gravada)}</div></div>
                  <div><div className="k">Exento / no obj.</div><div className="v mut">{fmt(calc.exento_no_objeto)}</div></div>
                  <div><div className="k">Imp. terceros</div><div className="v mut">{fmt(calc.impuestos_terceros)}</div></div>
                  <div><div className="k">Utilidad</div><div className="v hi">{fmt(calc.utilidad_total)}</div></div>
                  <div><div className="k">IVA utilidad</div><div className="v hi">{fmt(calc.iva_utilidad)}</div></div>
                  <div><div className="k">Subtotal CFDI</div><div className="v">{fmt(calc.subtotal)}</div></div>
                  <div><div className="k">IVA trasladado</div><div className="v">{fmt(calc.iva_trasladado)}</div></div>
                  <div><div className="k">IVA por pagar</div><div className={`v ${calc.iva_por_pagar < 0 ? "bad" : "hi"}`}>{fmt(calc.iva_por_pagar)}</div></div>
                  <div><div className="k">Margen</div><div className="v hi">{calc.margen_pct}%</div></div>
                </div>
                <div className={`cuadre ${calc.cuadra ? "ok" : "bad"}`}>
                  <div><span>Suma del desglose</span><b className="mono">{fmt(calc.cuadre)}</b></div>
                  <div><span>Venta</span><b className="mono">{fmt(venta)}</b></div>
                  <div className="res">{calc.cuadra ? "Cuadra ✓" : "No cuadra"}</div>
                </div>
              </div>
            </div>
          )}

          {/* Comparación */}
          {calc && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card-h"><h3>Comparación</h3><span className="sub">Cabina vs método actual</span></div>
              <div className="card-b">
                <table style={{ fontSize: 13.5 }}>
                  <tbody>
                    <tr>
                      <td style={{ border: 0, padding: "7px 0", color: "var(--cv-muted)" }}>
                        Método actual del spreadsheet
                        <div style={{ fontSize: 11.5, color: "var(--cv-ghost)" }}>venta con IVA − costo con IVA</div>
                      </td>
                      <td className="r mono" style={{ border: 0, padding: "7px 0", fontSize: 17, color: "var(--cv-ghost)" }}>{fmt0(viejo)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: 0, padding: "7px 0" }}>
                        <b>Cabina de Ventas</b>
                        <div style={{ fontSize: 11.5, color: "var(--cv-muted)" }}>utilidad real, sin IVA</div>
                      </td>
                      <td className="r mono" style={{ border: 0, padding: "7px 0", fontSize: 17, color: "var(--cv-blue)", fontWeight: 600 }} data-testid="lab-utilidad-cabina">{fmt0(calc.utilidad_total)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: 0, borderTop: "1px solid var(--cv-line)", padding: "9px 0" }}>
                        <b>Diferencia</b>
                        <div style={{ fontSize: 11.5, color: "var(--cv-muted)" }}>{Math.abs(dif) > 0.5 ? "IVA que hoy se lee como utilidad" : "sin diferencia"}</div>
                      </td>
                      <td className="r mono" style={{ border: 0, borderTop: "1px solid var(--cv-line)", padding: "9px 0", fontSize: 17, fontWeight: 600, color: "var(--cv-night)" }}>{fmt0(dif)}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="rulebox" style={{ marginTop: 14 }}>
                  {esCostoSinVenta
                    ? <><b>Línea de costo sin venta.</b> No se le aplica la fórmula de utilidad: si se le aplicara, aparecería como pérdida falsa.</>
                    : (calc.utilidad_total > 0
                        ? <>Margen real de <b>{calc.margen_pct}%</b> sobre un subtotal de <b>{fmt0(calc.subtotal)}</b>.</>
                        : <><b>Este servicio va en pérdida.</b></>)}
                </div>
              </div>
            </div>
          )}

          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-h"><h3>Ejemplos rápidos</h3></div>
            <div className="card-b" style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {EJEMPLOS.map((e, i) => <button key={i} className="btn ghost sm" onClick={() => usarEjemplo(e)} data-testid={`ej-${i}`}>{e.l}</button>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
